"use client";

import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider } from "@/integrations/wordpress/WordPressMenuProvider";
const NAV_ITEMS = [{
  label: "Home",
  href: "/"
}, {
  label: "Product Catalog",
  href: "/MaterialCatalogPage"
}, {
  label: "About Us",
  href: "/OurGlobalReachPage"
}, {
  label: "Contact Us",
  href: "/ConnectWithSalesPage"
}];
function DesktopMenuConsumer() {
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {NAV_ITEMS.map((item, i) => <NavigationMenuItem key={item.label} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href} className="group relative text-white text-xs uppercase tracking-[0.15em] font-default font-semibold bg-transparent hover:bg-transparent focus:bg-transparent px-0 py-2 h-auto">
                <span className="relative inline-block">
                  {item.label}
                  <span className="absolute left-0 -bottom-1 h-px w-full bg-sky-500" />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  return <nav className="flex flex-col gap-2 mt-8 relative z-10">
      {NAV_ITEMS.map((item, i) => <Link key={item.label} data-index={i} to={item.href} onClick={onItemClick} className="group relative text-white text-3xl font-default font-bold uppercase tracking-tight py-4 border-b border-slate-800">
          <span className="relative inline-block">
            {item.label}
            <span className="absolute left-0 -bottom-1 h-px w-full bg-sky-500" />
          </span>
        </Link>)}
    </nav>;
}
export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="4035" id="main-navigation" className={`sticky top-0 z-50 w-full transition-all duration-300 ${scrolled ? "bg-slate-950/90 backdrop-blur-xl border-b border-slate-800 shadow-lg" : "bg-slate-950 border-b border-transparent"}`}>
      <div className="mx-auto flex h-[72px] w-full items-center justify-between px-6 md:px-12">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <span className="text-xl font-default font-bold tracking-tighter text-white">
            World Sky Building Materials | SAB PROFESSSIONAL
          </span>
        </Link>

        <div className="hidden lg:flex items-center justify-center flex-1">
          <MenuProvider menu_id="16">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        <div className="hidden lg:flex items-center gap-6 shrink-0">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-lg bg-sky-500 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-lg bg-sky-500" />
            </span>
            <span className="text-xs uppercase tracking-[0.2em] font-default font-semibold text-slate-400">
              SUPPLY ACTIVE
            </span>
          </div>
          <Button asChild className="h-10 px-5 bg-sky-500 text-white hover:bg-sky-600 text-xs uppercase tracking-[0.15em] font-default font-bold rounded-lg">
            <Link to="/ConnectWithSalesPage">REQUEST QUOTE</Link>
          </Button>
        </div>

        <div className="lg:hidden flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-lg bg-sky-500 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-lg bg-sky-500" />
            </span>
            <span className="text-[10px] uppercase tracking-[0.2em] font-default font-semibold text-slate-400">
              LIVE
            </span>
          </div>
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open menu" className="text-white hover:bg-slate-900">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-slate-950 text-white border-l border-slate-800 w-full sm:max-w-md p-6 overflow-hidden">
              <SheetHeader className="p-0">
                <SheetTitle className="sr-only">Navigation</SheetTitle>
              </SheetHeader>

              <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{
              backgroundImage: "repeating-linear-gradient(0deg, var(--primary) 0, var(--primary) 1px, transparent 1px, transparent 14px)",
              animation: "wvcDataStream 8s linear infinite"
            }} />

              <style>{`
                @keyframes wvcDataStream {
                  0% { background-position: 0 0; }
                  100% { background-position: 0 -200px; }
                }
              `}</style>

              <div className="relative z-10 mt-4">
                <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-mono">
                  / NAVIGATION
                </div>
                <MenuProvider menu_id="16">
                  <MobileMenuConsumer onItemClick={() => setMobileOpen(false)} />
                </MenuProvider>

                <div className="mt-10">
                  <Button asChild className="w-full h-12 bg-sky-500 text-white hover:bg-sky-600 text-xs uppercase tracking-[0.2em] font-default font-bold rounded-lg">
                    <Link to="/ConnectWithSalesPage" onClick={() => setMobileOpen(false)}>
                      REQUEST QUOTE
                    </Link>
                  </Button>
                </div>

                <div className="mt-10 pt-6 border-t border-slate-800">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-mono mb-2">
                    / CONTACT
                  </div>
                  <div className="text-sm text-white font-default">
                    sales@worldsky.ae
                  </div>
                  <div className="text-sm text-slate-500 font-default">
                    +971 6 555 0000
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </section>;
}